import { Component, OnDestroy, OnInit } from '@angular/core';
import { Order } from "../../../model/order";
import { Document } from "../../../model/document";
import { Property } from "../../../model/property";
import {Observable, Subject, Subscription} from "rxjs";
import { OrderService } from "../../../service/order.service";
import { PropertyService } from "../../../service/property.service";
import { Router } from "@angular/router";
import { DownloadService } from "../../../service/download.service";
import { StorageService } from "../../../service/storage.service";
import { CustomerInteractionService } from "../../../service/customer-interaction.service";
import { KeyValue, Location } from "@angular/common";
import { debounce } from "lodash";
import { PermissionService } from "../../../service/permission.service";
import { UserGroup } from "../../../model/userGroup";
import { PermissionEntry } from "../../../model/permissionEntry";
import { MatDialog } from "@angular/material/dialog";
import { EditUserGroupDialogComponent } from "../../dialogs/edit-user-group-dialog/edit-user-group-dialog.component";
import { FormControl, UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { UserGroupDto } from "../../../model/userGroupDto";
import { LoginIdService } from "../../../service/login-id.service";
import {filter, map, startWith, takeUntil} from "rxjs/operators";
import { SnackbarService } from "../../../service/snackbar.service";
import {faArrowLeft} from "@fortawesome/free-solid-svg-icons";
import * as Sentry from "@sentry/angular";

interface DialogAction {
  action: string,
  data: UserGroup
}

@Component({
  selector: 'app-order-permissions',
  templateUrl: './order-permissions.component.html',
  styleUrls: ['./order-permissions.component.css']
})
export class OrderPermissionsComponent implements OnInit, OnDestroy {
  protected readonly faArrowLeft = faArrowLeft;

  orders: Order[] = [];
  documents: Document [] = [];
  userGroups: UserGroup[] = [];
  document: Document;
  searchText: string;
  propertiesBySearchTerm: Property[] = [];
  foundProperties: boolean = false;
  batchStarted: boolean = false;
  indexDBOrderObserver: Observable<Order>;
  toggle: boolean = true;

  page: number = 0;
  pageSize: number = 25;
  lastPage: boolean = true;
  controlGroup: UntypedFormGroup;
  selectedUserGroup = new FormControl();
  companyLoginSubscription: Subscription;
  isAllowedToEdit: boolean = false;
  requestedOrders: boolean = false;
  private abortController: AbortController | null = null;
  private stopRequestObservable: Subject<void> = new Subject<void>();

  constructor(private orderService: OrderService,
              private permissionService: PermissionService,
              private loginIdService: LoginIdService,
              private snackbarService: SnackbarService,
              private propertyService: PropertyService,
              private router: Router,
              private location: Location,
              private downloadService: DownloadService,
              private storageService: StorageService,
              private customerInteractionService: CustomerInteractionService,
              private formBuilder: UntypedFormBuilder,
              public dialog: MatDialog) {
    this.controlGroup = this.formBuilder.group({
        changeAll : false
    });
    this.checkPermissions();
    this.searchProperties = debounce(this.searchProperties, 500);
  }

  async ngOnInit(): Promise<any> {
    // wait until the loginIdService has been initialized
    await this.loginIdService.loginServiceInitialized$.toPromise();

    this.selectedUserGroup.setValue(this.checkSelectedUserGroup());
    this.selectedUserGroup.valueChanges.subscribe((value) => {
      sessionStorage.setItem('selectedUserGroup', value);
      this.updatePermissions();
    });

    /** Observer that listens to changes in the sessionStorage for the object with the key "orders". Returns the value of "orders".
     Used to update the view after changing routes, coming back to /order */
    this.indexDBOrderObserver = this.storageService.indexDBChange$.pipe(
        filter(({key}) => key === "orders"),
        map(orders => orders?.value),
        filter(value => value?.companyLoginId === this.loginIdService.getCurrentLoginId())
    );

    /** Subscribe to the sessionOrderObserver to detect changes */
    this.indexDBOrderObserver.subscribe((data) => {
      this.orders.forEach((o, index) => {
        if (o.orderid === data.orderid) {
          this.orders[index] = data;
        }
      })
    });

    this.companyLoginSubscription = this.loginIdService.loginIdChanged$.pipe(startWith(true)).subscribe( () => {
      if(this.checkPermissions()) {
        this.cancelOrderStatisticRequests();
        this.orders = [];
        this.requestedOrders = false;
        this.addOrders();
        this.selectedUserGroup.setValue(this.checkSelectedUserGroup());
      }
    });
  }

  private checkPermissions(): boolean {
    this.isAllowedToEdit = this.loginIdService.isAdmin();
    return this.isAllowedToEdit;
  }

  private getUserGroups(): void {
    if(this.loginIdService.getCurrentLoginId()) {
      this.permissionService.getUserGroupsFromCompany(this.loginIdService.getCurrentLoginId()).toPromise().then(data => {
        this.userGroups = [];
        data.forEach( userGroupDto => {
          this.userGroups.push(new UserGroup(userGroupDto));
        })
      }).then( () => {
        this.updatePermissions();
      });
    } else {
      console.error("LoginIdService is not initialized yet!");
    }
  }

  private checkSelectedUserGroup(): string {
    return (sessionStorage.getItem('selectedUserGroup') !== null
        && this.userGroups.length <  parseInt(sessionStorage.getItem('selectedUserGroup'))) ? sessionStorage.getItem('selectedUserGroup') : "0";
  }

  private getOptions(showSpinner: boolean) {
    let httpOptions = {
      params: undefined,
        headers: showSpinner ? this.loginIdService.getCurrentHeaders() : this.loginIdService.getCurrentCustomHeadersWithoutSpinner()
    }
    httpOptions.params = this.getRequestParams(this.page, this.pageSize)
    return httpOptions;
  }

  getRequestParams(page: number, pageSize: number): any {
    let params: any = {};
    if (page) {
      params[`page`] = page;
    }
    if (pageSize) {
      params[`size`] = pageSize;
    }
    return params;
  }

  updatePermissions() {
    const userGroupIndex = this.selectedUserGroup.value;
    // reset all orders permissions when userGroup or permissions have changed
    this.orders.forEach(order => {
      order.permission = null;
    });

    if (this.userGroups.length === 0) {
      return;
    }

    // check the active UserGroup permissions and see if they contain permissions for an order
    if(this.userGroups[userGroupIndex]) { // does the selected UserGroup have Permissions?
      this.userGroups[userGroupIndex].permissions.forEach((permissionEntry, key) => {
        const order = this.orders.find(order => order.orderid === permissionEntry.entryId);
        if(order) { // if there are permissions for an order assign them
          order.permission = permissionEntry;
        }
      })
    }
  }

  setPermissionEntry(orderId: string, scope: string): void {
    const userGroupIndex = this.selectedUserGroup.value;
    let permissions = this.userGroups[userGroupIndex].permissions;
    let entry = permissions.get(orderId);

    if(!this.controlGroup.value['changeAll']) { // change single permissionEntry
      if(entry) { // entry exists
        if(!entry.scopes.includes(scope)) { // check if it does not contain the scope yet
          entry.scopes.push(scope) // add the scope
        } else { // scope is already present?
          entry.scopes = entry.scopes.filter(value => value !== scope); // remove scope
          if(scope === 'VIEW') entry.scopes = entry.scopes.filter(value => value !== 'DOWNLOAD'); // if remove view, remove download too
          if(entry.scopes.length === 0) { // if no scopes left remove the entry completely
            this.userGroups[userGroupIndex].permissions.delete(orderId);
            this.orders.find(order => order.orderid === orderId).permission = null;
          }
        }
      } else { // no entry yet => create a new one with the scope
        let newEntry:PermissionEntry = new PermissionEntry;
        newEntry.type = "Order";
        newEntry.entryId = orderId;
        newEntry.scopes = [scope];
        if(scope === 'DOWNLOAD' && !newEntry.scopes.includes('VIEW')) { // if download then also view
          newEntry.scopes.push('VIEW');
        }
        newEntry.userGroupId = this.userGroups[userGroupIndex].id;
        permissions.set(orderId, newEntry);
        this.orders.find(order => order.orderid === orderId).permission = newEntry;
      }
    } else { // change all permissionEntries
      if(this.toggle) { // write permissions
        this.userGroups[userGroupIndex].permissions.forEach((entry, key) => {
          if(entry.type === "Order") {
            this.userGroups[userGroupIndex].permissions.delete(key);
          }
        });
        this.orders.forEach(order => {
          let newEntry:PermissionEntry = new PermissionEntry;
          newEntry.type = "Order";
          newEntry.entryId = order.orderid;
          newEntry.scopes = [scope];
          if(scope === 'DOWNLOAD' && !newEntry.scopes.includes('VIEW')) { // if download then also view
            newEntry.scopes.push('VIEW');
          }
          newEntry.userGroupId = this.userGroups[userGroupIndex].id;
          order.permission = newEntry;
          this.userGroups[userGroupIndex].permissions.set(newEntry.entryId, newEntry);
        })
      } else { // remove permissions
        this.userGroups[userGroupIndex].permissions.forEach((entry, key) => {
          if(entry.type === "Order") {
            this.userGroups[userGroupIndex].permissions.delete(key);
          }
        });
        this.orders.forEach(order => {
          order.permission = null;
        })
      }
      this.toggle = !this.toggle;
    }
  }

  savePermissions() {
    const userGroupIndex = this.selectedUserGroup.value;
    const updatedUserGroup = this.userGroups[userGroupIndex];
    const userGroupDto = new UserGroupDto(updatedUserGroup);

    this.permissionService.updateUserGroup(userGroupDto).toPromise()
      .then( () => {
        this.snackbarService.showSnackbar("Berechtigungen gespeichert!", true);
        this.updatePermissions();
      }).catch( (error) => {
        Sentry.captureException(error);
        this.snackbarService.showSnackbar("Fehler beim Speichern!", false);
    });
  }

  handleNextPage() {
    this.page++;
    this.addOrders();
  }

  searchProperties() {
    if (this.searchText.length > 0) {
      let observable = this.propertyService.findPropertiesBySearchTerm(this.searchText, this.getOptions(true));
      observable.toPromise().then(data => {
        // TODO: Use when permissions for properties are implemented
        // data = data.filter(property => this.loginIdService.hasPermissionForProperty(property, 'VIEW'))
        this.propertiesBySearchTerm = data;
      });
    } else {
      this.propertiesBySearchTerm = [];
    }
  }

  downloadForOrder(order: Order, onlyNew = false) {
    this.batchStarted = this.downloadService.downloadForOrder(order, onlyNew);
  }

  amountOfNewDocs(order: Order) {
    if(order.orderStatistics.numberOfNotDownloadedDocumentFiles){
      return order.orderStatistics.numberOfNotDownloadedDocumentFiles;
    }
    else{
      return 0;
    }
  }

  amountOfDocs(order: Order) {
    if(order.orderStatistics.numberOfDocumentFiles){
      return order.orderStatistics.numberOfDocumentFiles;
    }
    else{
      return 0;
    }
  }

  progress(order: Order) {
    if(order.orderStatistics.numberOfDocuments && order.orderStatistics.numberOfDocuments > 0){
      return order.orderStatistics.numberOfReceivedDocuments / order.orderStatistics.numberOfDocuments * 100;
    }
    else{
      return 0;
    }
  }

  hasOrderPropertyWithNecessaryActions(order) {
    for (const property of order.properties) {
      for (const customerInteraction of property.customerInteractions) {
        if (customerInteraction.status.toString() === "OPEN") {
          return true;
        }
      }
    }
    return false;
  }

  containsOrderDocumentWithUnseenInformation(order) {
    for (const property of order.properties) {
      for (const document of property.documents) {
        if (document.statusInformation.find(s => s.seen === false)) {
          return true;
        }
      }
    }
    return false;
  }

  hasPropertyNecessaryActions(property: Property) {
    for (const customerInteraction of property.customerInteractions) {
      if (customerInteraction.status.toString() === "OPEN") {
        return true;
      }
    }
    return false;
  }

  containsPropertyDocumentWithUnseenInformation(property) {
    for (const document of property.documents) {
      if (document.statusInformation.find(s => s.seen === false)) {
        return true;
      }
    }
    return false;
  }

  setFoundProperties() {
    let numberOfFoundProperties = document.querySelectorAll('#outer-container #property').length;
    this.foundProperties = numberOfFoundProperties > 0;
  }

  hasOrderProperties(order: Order) {
    return order.properties.length > 0
  }

  sortOrders(orders: Array<Order>): Array<Order> {
    return orders.sort((a, b) => new Date(b.entrydate).getTime() - new Date(a.entrydate).getTime());
  }

  orderIdDescending = (
      a: KeyValue<number, Order>,
      b: KeyValue<number, Order>
  ): number => {
    return b.value.orderid.localeCompare(a.value.orderid);
  }

  /**
   * Save orders inside the indexedDb of the browser.
   * Add orders to "this.orders" to update the view
   */
  addOrders() {
    this.orderService.getMultiUserOrders(false, this.getOptions(false)).subscribe(async (orders) => {
        this.requestedOrders = true;
        this.orders = orders;
        if (orders.length < this.pageSize) {
            this.lastPage = true;
        }
        for (let order of orders) {
            order.companyLoginId = this.loginIdService.getCurrentLoginId();
            if (!this.loginIdService.isAdmin()) {
                order.permission = this.loginIdService.getOrderPermissionEntry(order.orderid);
            }
        }

        // when orders are set apply the permissions via the userGroups
        this.getUserGroups();

        await this.setOrderStatistics();
    })
  }

    /**
     * Gets and set the order statistics for each order of this.orders
     */
    private async setOrderStatistics(): Promise<void> {
        if(this.abortController){
            this.abortController.abort();
        }

        this.abortController = new AbortController();
        const signal = this.abortController.signal;

        const batchSize = 5;
        const totalOrders = this.orders.length;

        for (let i = 0; i < totalOrders; i += batchSize) {
            const batchOrders = this.orders.slice(i, i + batchSize);

            const promises = batchOrders.map(async (order) => {
                if(signal.aborted){
                    return;
                }
                order.orderStatistics = await this.orderService.getOrderStatistics(order.orderid, this.getOptions(false))
                        .pipe(takeUntil(this.stopRequestObservable)).toPromise();
                await this.storageService.setIndexedDbItem({ key: "orders", value: order });
            });

            await Promise.all(promises);
        }
    }

  hasOrderStatistics(order: Order) {
    return !!(order.orderStatistics);
  }

  openUserGroupDialog() {
    const value = this.selectedUserGroup.value;
    const dialogRef = this.dialog.open(EditUserGroupDialogComponent, {
      width: '900px',
      data: {groups: this.userGroups, active: value},
      panelClass: 'altered-dialog-container'
    });

    dialogRef.afterClosed().subscribe(result => {
     if(result) {
       if(result.action === "create") {
         const userGroupDto = new UserGroupDto(result.data);
         this.permissionService.createUserGroup(userGroupDto).subscribe( response => {
           let group = new UserGroup(response)
           this.userGroups.push(group);
           this.setUpUserPermissions( group, result.invites.mails);
           this.snackbarService.showSnackbar("Nutzergruppe erfolgreich erstellt", true);
         }, error => {
           this.snackbarService.showSnackbar("Fehler beim Erstellen der Nutzergruppe!", false);
         });
       }

       if(result.action === "edit") {
         const userGroupDto = new UserGroupDto(result.data);
         this.permissionService.updateUserGroup(userGroupDto).subscribe( response => {
           const index = this.userGroups.findIndex( elem => elem.id === response.id);
           if(index !== -1) {
             this.userGroups = this.userGroups.map(grp => {
               if(grp.id === result.data.userGroupId) {
                 return result.data;
               }
               return grp;
             });
             this.snackbarService.showSnackbar("Nutzergruppe erfolgreich aktualisiert", true);
           } else {
             this.snackbarService.showSnackbar("Nutzergruppe konnte nicht aktualisiert werden", false);
           }
         });
       }

       if(result.action === "delete") {
         const userGroupDto = new UserGroupDto(result.data);
         this.permissionService.deleteUserGroup(userGroupDto.companyLoginId, userGroupDto.id).subscribe(response => {
           this.snackbarService.showSnackbar("Nutzergruppe erfolgreich gelöscht", true);
           const index = this.userGroups.findIndex(group => group.id === userGroupDto.id);
           this.userGroups.splice(index, 1);
         }, error => {
           this.snackbarService.showSnackbar("Nutzergruppe konnte nicht gelöscht werden!", false);
         })
       }
     } // do nothing, just close
    });
  }

  setUpUserPermissions(group: UserGroup, mails: string[]): void {
    const data = {
      loginId: group.companyLoginId,
      groupId: group.id,
      userMails: mails
    }

    this.permissionService.createInvitesAndAssignPermissions(data).subscribe(res => {
      if(res.length > 0) {
        this.snackbarService.showSnackbar("Nutzer erfolgreich eingeladen", true);
      }
    }, error => {
      this.snackbarService.showSnackbar("Es ist ein Fehler aufgetreten!", false);
    });
  }

    navigateBack() {
      this.router.navigate(['/order']);
    }

    /**
     * Cancel all current requests and prevent future requests to start
     */
    private cancelOrderStatisticRequests(): void{
        if (this.abortController) {
            this.abortController.abort();
        }
        this.stopRequestObservable.next();
    }

  ngOnDestroy(): void {
    this.cancelOrderStatisticRequests();
    this.companyLoginSubscription.unsubscribe();
  }
}
